// Çoklu gösterge seçici modalı (M16 T18, DR9): aynı dosyayı birden çok gösterge × yıl hücresine
// bağlama. Grid'deki yaprak/türetilmemiş göstergeler satır, yıllar sütun; varsayılan yalnız açık
// hücre işaretli, "Aynı kategoridekileri seç" kısayolu. İsteğe bağlı konum (Excel sayfa/hücre
// ya da PDF sayfa no). Seçim mantığı lib/kpi-evidence-ui.ts'te (vitest).

import { useMemo, useState } from "react";
import type { KpiEvidenceLocator, KpiResolvedIndicator } from "@mahir/shared";
import { KPI_EVIDENCE_COPY } from "../../lib/kpi-evidence-copy";
import {
  buildEvidenceTargetOptions,
  defaultTargetKeys,
  fileKindLabel,
  sameCategoryKeys,
  targetKey,
  targetsFromKeys,
} from "../../lib/kpi-evidence-ui";
import {
  btnGhost,
  btnPrimary,
  btnSmall,
  inputStyle,
  labelStyle,
  pillStyle,
} from "./kpi-form-styles";
import { KpiModal } from "./KpiModal";

export interface EvidenceTargetPickerProps {
  open: boolean;
  file: { fileName: string; mimeType: string | null };
  indicators: KpiResolvedIndicator[];
  years: number[];
  entityId: string;
  current: { metricId: string; year: number };
  busy: boolean;
  error: string | null;
  onConfirm: (args: {
    targets: { metricId: string; entityId: string; year: number }[];
    locator?: KpiEvidenceLocator;
  }) => void;
  onClose: () => void;
}

const P = KPI_EVIDENCE_COPY.picker;

export function EvidenceTargetPicker(props: EvidenceTargetPickerProps): JSX.Element | null {
  const { open, file, indicators, years, entityId, current, busy, error, onConfirm, onClose } =
    props;
  const options = useMemo(
    () => buildEvidenceTargetOptions(indicators, current.metricId),
    [indicators, current.metricId],
  );
  const yearsDesc = useMemo(() => [...years].sort((a, b) => b - a), [years]);
  const [keys, setKeys] = useState<Set<string>>(() =>
    defaultTargetKeys(current.metricId, current.year),
  );
  const [search, setSearch] = useState("");
  const [sheet, setSheet] = useState("");
  const [cellRef, setCellRef] = useState("");
  const [page, setPage] = useState("");
  const kind = fileKindLabel(file.mimeType, file.fileName);
  const isSheet = kind === "XLSX" || kind === "CSV";

  const visible = search.trim()
    ? options.filter((o) =>
        o.label.toLocaleLowerCase("tr").includes(search.toLocaleLowerCase("tr")),
      )
    : options;

  function toggle(key: string): void {
    setKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function locator(): KpiEvidenceLocator | undefined {
    if (isSheet) {
      return sheet.trim() && cellRef.trim()
        ? { sheet: sheet.trim(), cell: cellRef.trim().toUpperCase() }
        : undefined;
    }
    const n = Number.parseInt(page, 10);
    return Number.isInteger(n) && n > 0 ? { page: n } : undefined;
  }

  const loc = locator();

  return (
    <KpiModal
      open={open}
      kicker={P.kicker}
      title={P.title}
      description={`${file.fileName} — ${P.description}`}
      width={720}
      onClose={onClose}
      footer={
        <>
          <span className="text-xs mr-auto" style={{ color: "var(--color-text-3)" }}>
            {P.selectedCount(keys.size)}
          </span>
          <button type="button" style={btnGhost} onClick={onClose} disabled={busy}>
            {KPI_EVIDENCE_COPY.panel.cancel}
          </button>
          <button
            type="button"
            style={{ ...btnPrimary, opacity: keys.size === 0 || busy ? 0.5 : 1 }}
            disabled={keys.size === 0 || busy}
            aria-busy={busy || undefined}
            data-testid="kpi-evidence-picker-confirm"
            onClick={() =>
              onConfirm({
                targets: targetsFromKeys(keys, entityId),
                ...(loc ? { locator: loc } : {}),
              })
            }
          >
            {busy ? "…" : P.confirm}
          </button>
        </>
      }
    >
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <input
          aria-label={P.searchIndicators}
          placeholder={P.searchIndicators}
          style={{ ...inputStyle, width: 220, minHeight: 32, padding: "4px 10px" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button
          type="button"
          style={btnSmall}
          onClick={() => setKeys(sameCategoryKeys(options, current.year))}
        >
          {P.sameCategory}
        </button>
        <button type="button" style={btnSmall} onClick={() => setKeys(new Set())}>
          {P.clearAll}
        </button>
      </div>
      <div
        style={{
          maxHeight: 320,
          overflowY: "auto",
          border: "1px solid var(--color-border)",
          borderRadius: 8,
        }}
      >
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th
                style={{
                  ...labelStyle,
                  textAlign: "left",
                  padding: "6px 10px",
                  position: "sticky",
                  top: 0,
                  background: "var(--color-surface)",
                }}
              >
                Gösterge
              </th>
              {yearsDesc.map((y) => (
                <th
                  key={y}
                  style={{
                    ...labelStyle,
                    textAlign: "center",
                    padding: "6px 8px",
                    position: "sticky",
                    top: 0,
                    background: "var(--color-surface)",
                  }}
                >
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((o) => (
              <tr key={o.indicatorId} style={{ borderTop: "1px solid var(--color-border)" }}>
                <td
                  style={{ padding: "6px 10px", fontSize: "0.85rem", color: "var(--color-text)" }}
                >
                  {o.label}
                  {o.categoryLabel ? (
                    <span className="ml-2 text-xs" style={{ color: "var(--color-text-3)" }}>
                      {o.categoryLabel}
                    </span>
                  ) : null}
                  {o.isCurrent ? (
                    <span style={{ ...pillStyle, marginLeft: 6 }}>açık hücre</span>
                  ) : null}
                </td>
                {yearsDesc.map((y) => {
                  const k = targetKey(o.metricId, y);
                  return (
                    <td key={y} style={{ textAlign: "center", padding: "4px 8px" }}>
                      <input
                        type="checkbox"
                        aria-label={`${o.label} · ${y.toString()}`}
                        checked={keys.has(k)}
                        onChange={() => toggle(k)}
                        style={{ width: 16, height: 16 }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {visible.length === 0 ? (
              <tr>
                <td
                  colSpan={yearsDesc.length + 1}
                  style={{ padding: 12, color: "var(--color-text-3)", fontSize: "0.85rem" }}
                >
                  Eşleşen gösterge yok
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <div style={labelStyle}>{P.locatorLabel}</div>
        {isSheet ? (
          <div className="flex gap-2 mt-1">
            <input
              aria-label={P.locatorSheet}
              placeholder={P.locatorSheet}
              style={{ ...inputStyle, minHeight: 32, padding: "4px 10px" }}
              value={sheet}
              onChange={(e) => setSheet(e.target.value)}
            />
            <input
              aria-label={P.locatorCell}
              placeholder="F12"
              style={{ ...inputStyle, minHeight: 32, padding: "4px 10px", width: 120 }}
              value={cellRef}
              onChange={(e) => setCellRef(e.target.value)}
            />
          </div>
        ) : (
          <input
            aria-label={P.locatorPage}
            placeholder={P.locatorPage}
            inputMode="numeric"
            style={{ ...inputStyle, minHeight: 32, padding: "4px 10px", width: 140, marginTop: 4 }}
            value={page}
            onChange={(e) => setPage(e.target.value)}
          />
        )}
      </div>
      {error ? (
        <div role="alert" className="text-xs mt-2" style={{ color: "var(--color-error)" }}>
          {error}
        </div>
      ) : null}
    </KpiModal>
  );
}
